// Fastholder cursoren visuelt ved navigationens udgangspunkt under en drag-baseret ROTATION (EarthControls: højre knap).
//
// Problem: ved rotation fastlægger EarthControls et pivot-punkt i punktskyen (rotationskuglen), og rotationen er en rigid drejning om
// netop dette punkt - det ligger altså på samme skærmposition hele rotationen. Men musecursoren vandrer hen over skærmen, så
// brugerens visuelle "greb" (kuglen) og cursoren går fra hinanden.
//
// Løsning: Pointer Lock. Når rotationen reelt er i gang (musen flyttet > LOCK_SLOP_PX), låses markøren til canvas'et: browseren skjuler
// den og leverer kun bevægelsesdeltaer (movementX/Y). I stedet tegnes en virtuel cursor ved låsepositionen (= rotationskuglen), og
// musebevægelsen styrer stadig rotationen. Når knappen slippes, frigives låsen, og browseren genopretter den rigtige cursor præcis
// dér, hvor den blev låst - ingen spring.
//
// Eksisterende kode (InputHandler/EarthControls og alle værktøjer, der læser clientX/clientY og mouseup-position for klik-vs-træk, fx
// idle-højreklik-reglen i digitaliseringen) forudsætter, at museeventenes clientX/clientY følger musen. Under en lås er de native
// koordinater frosne, så de native mousemove/mouseup afskærmes (window, capture) og gendannes som syntetiske MouseEvents med den
// VIRTUELLE position (låsepositionen + akkumuleret bevægelse). Dermed ser alt øvrigt præcis det samme som uden lås.
//
// Kun når styringen vil have det (controls.wantsCursorLock(buttons)); pan (venstre knap) er uberørt - dér følger det greb'ede punkt
// allerede musen (stråle/plan-skæring), så cursoren ligger hele tiden på grebspunktet.
//
// Sikker frigivelse: mouseup, Escape (browseren forlader låsen -> pointerlockchange), vindue-/fanefokus tabt, pointerlockerror og
// fjernelse af styringen. Efter en uventet frigivelse genbaseres træk-positionen på den rigtige cursor (ingen hop i kameraet).

export const LOCK_SLOP_PX = 3;

// Pure: er musen flyttet nok fra mousedown til, at rotationen reelt er i gang (og dermed værd at låse)?
export function movedBeyondSlop(down, pos, slop = LOCK_SLOP_PX){
	return Math.hypot(pos.x - down.x, pos.y - down.y) > slop;
}

// Pure: den virtuelle position efter en lås-bevægelse
export function advanceVirtual(virtual, movementX, movementY){
	return {x: virtual.x + (movementX || 0), y: virtual.y + (movementY || 0)};
}

const CURSOR_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" style="display:block">' +
	'<path d="M2 1.5 L2 17 L6.2 13.2 L9 19.5 L11.6 18.3 L8.8 12.2 L14.2 11.8 Z" fill="#fff" stroke="#000" stroke-width="1.3" stroke-linejoin="round"/></svg>';

export class CursorAnchor {
	constructor(inputHandler){
		this.ih = inputHandler;
		this.canvas = inputHandler.domElement;
		this.state = "idle"; // idle | armed | requesting | locked | fallback
		this.button = null;
		this.down = null;
		this.last = null;
		this.virtual = null;
		this.cancelled = false;
		this.cursorEl = null;

		this._onMove = this._onMove.bind(this);
		this._onUp = this._onUp.bind(this);
		this._onLockChange = this._onLockChange.bind(this);
		this._onLockError = this._onLockError.bind(this);
		this._onFocusLoss = this._onFocusLoss.bind(this);

		this.supported = typeof document !== "undefined" && "pointerLockElement" in document &&
			typeof this.canvas.requestPointerLock === "function";
	}

	_controls(){
		const viewer = this.ih.viewer;
		return viewer && typeof viewer.getControls === "function" ? viewer.getControls() : null;
	}

	_wants(buttons){
		const controls = this._controls();
		return !!controls && typeof controls.wantsCursorLock === "function" && controls.wantsCursorLock(buttons);
	}

	// kaldes fra InputHandler.onMouseDown, efter at kamera-drag er startet
	arm(e){
		if(!this.supported || this.state !== "idle") return;
		if(!this.ih.drag || this.ih.drag.object) return; // kun kamera-navigation, aldrig objekt-træk
		if(!this._wants(e.buttons)) return;

		this.state = "armed";
		this.button = e.button;
		this.cancelled = false;
		this.down = {x: e.clientX, y: e.clientY};
		this.last = {x: e.clientX, y: e.clientY};

		window.addEventListener("mousemove", this._onMove, true);
		window.addEventListener("mouseup", this._onUp, true);
		window.addEventListener("blur", this._onFocusLoss);
		document.addEventListener("pointerlockchange", this._onLockChange);
		document.addEventListener("pointerlockerror", this._onLockError);
		document.addEventListener("visibilitychange", this._onFocusLoss);
	}

	_synthesize(type, native, pos, movementX = 0, movementY = 0){
		const evt = new MouseEvent(type, {
			bubbles: true, cancelable: true, composed: true, view: window,
			clientX: pos.x, clientY: pos.y,
			screenX: native.screenX, screenY: native.screenY,
			button: native.button, buttons: native.buttons,
			ctrlKey: native.ctrlKey, shiftKey: native.shiftKey, altKey: native.altKey, metaKey: native.metaKey,
			movementX, movementY,
		});
		evt.__cursorAnchor = true;
		this.canvas.dispatchEvent(evt);
	}

	_onMove(e){
		if(e.__cursorAnchor) return; // vores egen gendannede begivenhed
		if(this.state === "locked"){
			e.stopImmediatePropagation();
			this.virtual = advanceVirtual(this.virtual, e.movementX, e.movementY);
			this._synthesize("mousemove", e, this.virtual, e.movementX, e.movementY);
			return;
		}

		if(this.state === "fallback"){
			// låsen blev frigivet uventet midt i et træk: genbaser træk-positionen på den rigtige cursor, så kameraet ikke hopper
			const rect = this.canvas.getBoundingClientRect();
			if(this.ih.drag) this.ih.drag.end.set(e.clientX - rect.left, e.clientY - rect.top);
			this._finish(false);
			return;
		}

		this.last = {x: e.clientX, y: e.clientY};
		if(this.state === "armed" && movedBeyondSlop(this.down, this.last) && this._wants(e.buttons)){
			this.state = "requesting";
			try{
				const result = this.canvas.requestPointerLock();
				if(result && typeof result.catch === "function") result.catch(() => this._onLockError());
			}catch(error){
				this._onLockError();
			}
		}
	}

	_onUp(e){
		if(e.__cursorAnchor) return;
		if(e.button !== this.button) return;
		if(this.state === "locked"){
			e.stopImmediatePropagation();
			this._synthesize("mouseup", e, this.virtual);
		}
		this._finish(true);
	}

	_onLockChange(){
		const locked = document.pointerLockElement === this.canvas;
		if(locked){
			if(this.state === "requesting" && !this.cancelled){
				this.state = "locked";
				this.virtual = {x: this.last.x, y: this.last.y};
				this._showCursor(this.virtual);
			}else{
				this._exitLock();
			}
		}else if(this.state === "locked"){
			// Escape / fokus tabt / browseren afsluttede låsen
			this._hideCursor();
			this.state = "fallback";
		}
	}

	_onLockError(){
		if(this.state === "requesting") this._finish(false);
	}

	_onFocusLoss(){
		if(this.state === "requesting") this.cancelled = true;
		if(this.state === "locked" || this.state === "requesting") this._exitLock();
	}

	_exitLock(){
		if(document.pointerLockElement === this.canvas && typeof document.exitPointerLock === "function"){
			document.exitPointerLock();
		}
	}

	_showCursor(pos){
		if(this.cursorEl) return;
		const el = document.createElement("div");
		el.className = "potree_cursor_anchor";
		el.style.cssText = "position:fixed;left:" + pos.x + "px;top:" + pos.y + "px;width:20px;height:22px;pointer-events:none;z-index:2147483000;";
		el.innerHTML = CURSOR_SVG;
		document.body.appendChild(el);
		this.cursorEl = el;
	}

	_hideCursor(){
		if(this.cursorEl && this.cursorEl.parentNode) this.cursorEl.parentNode.removeChild(this.cursorEl);
		this.cursorEl = null;
	}

	// slutter navigationen: frigiv lås, fjern virtuel cursor og lyttere
	_finish(exitLock){
		if(exitLock){
			// en lås, der først bevilges EFTER at knappen er sluppet (anmodet, men ikke bevilget endnu), må aldrig blive hængende
			if(this.state === "requesting"){
				const release = () => {
					document.removeEventListener("pointerlockchange", release);
					this._exitLock();
				};
				document.addEventListener("pointerlockchange", release);
				setTimeout(() => document.removeEventListener("pointerlockchange", release), 1500);
			}
			this._exitLock();
		}
		this._hideCursor();
		window.removeEventListener("mousemove", this._onMove, true);
		window.removeEventListener("mouseup", this._onUp, true);
		window.removeEventListener("blur", this._onFocusLoss);
		document.removeEventListener("pointerlockchange", this._onLockChange);
		document.removeEventListener("pointerlockerror", this._onLockError);
		document.removeEventListener("visibilitychange", this._onFocusLoss);
		this.state = "idle";
		this.button = null;
		this.down = null;
		this.virtual = null;
	}

	dispose(){
		if(this.state !== "idle") this._finish(true);
	}
}
